import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type { Theme, ThemeProviderState } from './theme-context'
import { ThemeProviderContext } from './theme-context'

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

export default function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'wpc-ui-theme',
  ...props
}: ThemeProviderProps) {
  // SSR-safe initial state - read from localStorage on initialization
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      const persisted =
        typeof window !== 'undefined' ? (localStorage.getItem(storageKey) as Theme | null) : null

      return persisted ?? defaultTheme
    } catch {
      return defaultTheme
    }
  })

  // Apply theme to <html>
  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement

    root.classList.remove('light', 'dark')

    if (theme === 'system') {
      const mql = window.matchMedia('(prefers-color-scheme: dark)')

      const apply = (ev?: MediaQueryListEvent) => {
        const isDark = typeof ev?.matches === 'boolean' ? ev.matches : mql.matches

        root.classList.remove('light', 'dark')
        root.classList.add(isDark ? 'dark' : 'light')
      }

      // set immediately and subscribe
      apply()

      if ('addEventListener' in mql) {
        mql.addEventListener('change', apply)
        return () => mql.removeEventListener('change', apply)
      } else {
        // legacy Safari/old Chromium
        // @ts-expect-error legacy API
        mql.addListener?.(apply)
        return () => {
          // @ts-expect-error legacy API
          mql.removeListener?.(apply)
        }
      }
    } else {
      root.classList.add(theme)
    }
  }, [theme])

  const getResolvedTheme = useCallback((): 'dark' | 'light' => {
    if (theme === 'system') {
      if (typeof window === 'undefined') return 'light'
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }

    return theme
  }, [theme])

  // Avoid shadowing React's state setter; memoize to keep stable identity
  const setTheme = useCallback(
    (next: Theme) => {
      try {
        localStorage.setItem(storageKey, next)
      } catch {
        /* ignore write failures (privacy mode, etc.) */
      }

      setThemeState(next)
    },
    [storageKey]
  )

  const value = useMemo<ThemeProviderState>(
    () => ({ theme, setTheme, getResolvedTheme }),
    [theme, setTheme, getResolvedTheme]
  )

  return (
    <ThemeProviderContext.Provider value={value} {...props}>
      {children}
    </ThemeProviderContext.Provider>
  )
}
